import React, { useEffect, useState, useCallback } from 'react';
import { FaFilter, FaChevronUp, FaChevronDown } from 'react-icons/fa';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import NoRecordFound from '../../../components/common/no-record-found';

const ROWS_PER_PAGE = 50;

const SalesContractDeadlineMonitor = () => {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  
  // NEW: Grand Total state
  const [grandTotals, setGrandTotals] = useState(null);

  // Print states
  const [isPrinting, setIsPrinting] = useState(false);
  const [printData, setPrintData] = useState([]);

  // Filter states
  const [filters, setFilters] = useState({
    salesContractNo: '',
    buyerName: '',
    commodity: '',
    vesselName: '',
    filterBalanceQuantity: false
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  // Debounce search
  useEffect(() => {
    const delay = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(delay);
  }, [search]);

  // Debounce filters
  useEffect(() => {
    const delay = setTimeout(() => setDebouncedFilters(filters), 400);
    return () => clearTimeout(delay);
  }, [filters]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ 
        page, 
        limit: ROWS_PER_PAGE, 
        search: debouncedSearch.trim(),
        salesContractNo: debouncedFilters.salesContractNo.trim(),
        buyerName: debouncedFilters.buyerName.trim(),
        commodity: debouncedFilters.commodity.trim(),
        vesselName: debouncedFilters.vesselName.trim(),
        filterBalanceQuantity: debouncedFilters.filterBalanceQuantity.toString()
      });
      const res = await fetch(`${API_BASE_URL}/api/report/sales-contract-deadline-monitor?${params.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const result = await res.json();
      setData(result.data || []);
      setTotal(result.total || result.data?.length || 0);
      
      // NEW: Set grand totals from backend
      if (result.grandTotals) {
        setGrandTotals(result.grandTotals);
      }
    } catch (err) {
      setError('Failed to load sales contract deadline monitor');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, debouncedFilters]);

  useEffect(() => {
    fetchData();
  }, [debouncedSearch, debouncedFilters, page, fetchData]);

  const totalPages = Math.ceil(total / ROWS_PER_PAGE);
  
  // NEW: Check if current page is last page
  const isLastPage = page === totalPages;
  
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
    setPage(1);
  };

  const handleCheckboxChange = (filterName, checked) => {
    setFilters(prev => ({ ...prev, [filterName]: checked }));
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      salesContractNo: '',
      buyerName: '',
      commodity: '',
      vesselName: '',
      filterBalanceQuantity: false
    });
    setSearch('');
    setPage(1);
  };

  const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

  // Fetch ALL data for printing
  const fetchAllDataForPrint = async () => {
    setIsPrinting(true);
    try {
      const params = new URLSearchParams({ 
        page: 1, 
        limit: 999999,
        search: debouncedSearch.trim(),
        salesContractNo: debouncedFilters.salesContractNo.trim(),
        buyerName: debouncedFilters.buyerName.trim(),
        commodity: debouncedFilters.commodity.trim(),
        vesselName: debouncedFilters.vesselName.trim(),
        filterBalanceQuantity: debouncedFilters.filterBalanceQuantity.toString()
      });
      const res = await fetch(`${API_BASE_URL}/api/report/sales-contract-deadline-monitor?${params.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const result = await res.json();
      setPrintData(result.data || []);
      
      setTimeout(() => {
        window.print();
        setIsPrinting(false);
        setPrintData([]);
      }, 500);
    } catch (err) {
      alert('Error loading data for print. Please try again.');
      setIsPrinting(false);
    }
  };

  // Calculate totals for current page data
  const calculateTotals = (dataArray) => {
    return dataArray.reduce((acc, row) => {
      acc.saleQuantity += Math.max(0, parseFloat(row.sale_quantity) || 0);
      acc.soldQuantity += Math.max(0, parseFloat(row.sold_quantity) || 0);
      acc.balanceQuantity += Math.max(0, parseFloat(row.balance_quantity) || 0);
      acc.valueOfDeliveredQty += Math.max(0, parseFloat(row.value_of_delivered_qty) || 0);
      acc.salesContractAmount += Math.max(0, parseFloat(row.sales_contract_amount) || 0);
      acc.balanceToBeReceived += parseFloat(row.balance_to_be_received) || 0;
      acc.balanceAfterAdvanceAdj += parseFloat(row.balance_after_advance_adjustment) || 0;
      acc.advanceAmount += Math.max(0, parseFloat(row.advance_amount) || 0);
      acc.paymentAgainstSale += Math.max(0, parseFloat(row.payment_against_sale) || 0);
      return acc;
    }, {
      saleQuantity: 0,
      soldQuantity: 0,
      balanceQuantity: 0,
      valueOfDeliveredQty: 0,
      salesContractAmount: 0,
      balanceToBeReceived: 0,
      balanceAfterAdvanceAdj: 0,
      advanceAmount: 0,
      paymentAgainstSale: 0,
    });
  };

  const totals = calculateTotals(data);
  const printTotals = isPrinting ? calculateTotals(printData) : totals;

  const weightedAverageSalePricePerMT = totals.saleQuantity > 0 
    ? (totals.salesContractAmount / totals.saleQuantity) 
    : 0;

  const printWeightedAverage = printTotals.saleQuantity > 0 
    ? (printTotals.salesContractAmount / printTotals.saleQuantity) 
    : 0;

  // NEW: Grand Total weighted average
  const grandWeightedAverage = grandTotals && grandTotals.saleQuantity > 0
    ? (grandTotals.salesContractAmount / grandTotals.saleQuantity)
    : 0;

  // Render table rows
  const renderTableRows = (dataArray) => {
    return dataArray.map((row, index) => (
      <tr key={index} className="hover:bg-gray-50">
        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 border print-hide-col">{row.purchase_contract_no || '-'}</td>
        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 border print-hide-col">{row.sales_contract_no || '-'}</td>
        <td className="px-3 py-3 text-sm text-gray-900 border" title={row.buyer_name || '-'}>
          <div className="max-w-[150px] whitespace-nowrap overflow-hidden text-ellipsis">
            {row.buyer_name || '-'}
          </div>
        </td>
        <td className="px-3 py-3 text-sm text-gray-900 border" title={row.commodity_name || '-'}>
          <div className="max-w-[120px] whitespace-nowrap overflow-hidden text-ellipsis">
            {row.commodity_name || '-'}
          </div>
        </td>
        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 border">{row.vessel_name || '-'}</td>
        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 border text-center">{row.lifting_period_from || '-'}</td>
        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 border text-center">{row.lifting_period_to || '-'}</td>
        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 border text-right">
          {row.sale_quantity && parseFloat(row.sale_quantity) > 0 ? `${parseFloat(row.sale_quantity).toLocaleString('en-IN')} ` : '-'}
        </td>
        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 border text-right">
          {row.sold_quantity && parseFloat(row.sold_quantity) > 0 ? `${parseFloat(row.sold_quantity).toLocaleString('en-IN')} ` : '-'}
        </td>
        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 border text-right">
          {row.balance_quantity && parseFloat(row.balance_quantity) > 0 ? `${parseFloat(row.balance_quantity).toLocaleString('en-IN')} ` : '0'}
        </td>
        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 border text-right">
          {row.sale_price_per_mt && parseFloat(row.sale_price_per_mt) > 0 ? `₹${parseFloat(row.sale_price_per_mt).toLocaleString('en-IN')}` : '₹0'}
        </td>
        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 border text-right">
          {row.value_of_delivered_qty ? `₹${parseFloat(row.value_of_delivered_qty).toLocaleString('en-IN')}` : '₹0'}
        </td>
        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 border text-right">
          {row.advance_amount && parseFloat(row.advance_amount) > 0 ? `₹${parseFloat(row.advance_amount).toLocaleString('en-IN')}` : '₹0'}
        </td>
        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 border text-right">
          {row.payment_against_sale && parseFloat(row.payment_against_sale) > 0 ? `₹${parseFloat(row.payment_against_sale).toLocaleString('en-IN')}` : '₹0'}
        </td>
        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 border text-right">
          {row.sales_contract_amount && parseFloat(row.sales_contract_amount) > 0 ? `₹${parseFloat(row.sales_contract_amount).toLocaleString('en-IN')}` : '₹0'}
        </td>
        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 border text-right">
          {row.balance_to_be_received ? `₹${parseFloat(row.balance_to_be_received).toLocaleString('en-IN')}` : '₹0'}
        </td>
        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 border text-right">
          {row.balance_after_advance_adjustment ? `₹${parseFloat(row.balance_after_advance_adjustment).toLocaleString('en-IN')}` : '₹0'}
        </td>
      </tr>
    ));
  };

  return (
    <>
      {/* Print-specific CSS */}
      <style>
        {`
          @media print {
            .print-hide-col {
              display: none !important;
            }
            
            .no-print {
              display: none !important;
            }
            
            .print-only {
              display: block !important;
            }
            
            @page {
              size: A3 landscape;
              margin: 0.5cm;
            }
            
            body {
              background: white;
              margin: 0;
              padding: 0;
            }
            
            table {
              page-break-inside: auto;
              font-size: 9pt !important;
              width: 100% !important;
              border-collapse: collapse !important;
            }
            
            thead {
              display: table-header-group;
            }
            
            tfoot {
              display: table-footer-group;
              page-break-inside: avoid;
            }
            
            tr {
              page-break-inside: avoid;
              page-break-after: auto;
            }
            
            th, td {
              padding: 3px 5px !important;
              font-size: 9pt !important;
              word-wrap: break-word;
              overflow: hidden;
            }
            
            th {
              font-size: 8pt !important;
              font-weight: bold;
              line-height: 1.2;
            }
            
            .print-only h2 {
              font-size: 14pt;
              margin: 8px 0;
            }
            
            .max-w-8xl {
              max-width: 100% !important;
            }
            
            * {
              box-sizing: border-box;
            }
          }
          
          .print-only {
            display: none;
          }
        `}
      </style>

      {/* Print Loading Overlay */}
      {isPrinting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 no-print">
          <div className="bg-white rounded-lg p-6 shadow-xl">
            <div className="flex flex-col items-center space-y-3">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
              <p className="text-lg font-semibold text-gray-800">Loading all records for print...</p>
              <p className="text-sm text-gray-600">Please wait</p>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-8xl mx-auto p-6 bg-white shadow-sm border border-gray-100">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4 no-print">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Sale Contract Deadline Monitor</h2>
            <p className="text-sm text-gray-500 mt-1">Monitor sales contracts with payment and deadline tracking</p>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-[#10a5e9] rounded-md hover:bg-blue-100 text-sm w-full sm:w-auto justify-center"
            >
              <FaFilter />
              <span className="hidden sm:inline">{showFilters ? 'Hide Filters' : 'Show Filters'}</span>
              <span className="sm:hidden">{showFilters ? 'Hide' : 'Filters'}</span>
              {showFilters ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
            </button>

            <div className="relative w-full sm:w-80">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" strokeWidth={2} />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search all fields..."
                value={search}
                onChange={handleSearchChange}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition"
              />
            </div>

            <button
              onClick={fetchAllDataForPrint}
              disabled={isPrinting || loading || data.length === 0}
              className="flex items-center gap-2 px-3 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 text-sm w-full sm:w-auto justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              <span className="hidden sm:inline">Print</span>
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-6 shadow-sm animate-slideDown no-print">
            <div className="flex justify-end mb-3">
              <button
                onClick={handleClearFilters}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition text-sm w-full sm:w-auto"
              >
                Reset Filters
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                  <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Sales Contract No.
                </label>
                <input
                  type="text"
                  placeholder="Enter contract no..."
                  value={filters.salesContractNo}
                  onChange={(e) => handleFilterChange('salesContractNo', e.target.value)}
                  className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                  <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Buyer's Name
                </label>
                <input
                  type="text"
                  placeholder="Enter buyer name..."
                  value={filters.buyerName}
                  onChange={(e) => handleFilterChange('buyerName', e.target.value)}
                  className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                  <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                  Commodity
                </label>
                <input
                  type="text"
                  placeholder="Enter commodity..."
                  value={filters.commodity}
                  onChange={(e) => handleFilterChange('commodity', e.target.value)}
                  className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                  <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  Vessel Name
                </label>
                <input
                  type="text"
                  placeholder="Enter vessel name..."
                  value={filters.vesselName}
                  onChange={(e) => handleFilterChange('vesselName', e.target.value)}
                  className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                />
              </div>
            </div>
            
            {/* Balance Quantity Filter */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.filterBalanceQuantity}
                  onChange={(e) => handleCheckboxChange('filterBalanceQuantity', e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Filter Balance Quantity (Balance Quantity is ±10% of Contracted Quantity or equal to zero & Closing Date not entered)
                </span>
              </label>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center py-12 space-y-3">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600"></div>
            <p className="text-sm text-gray-600">Loading sales contract data...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg">
            <h3 className="text-sm font-medium text-red-800">Error loading data</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        ) : data.length === 0 ? (
          <NoRecordFound message="No sales contracts found" subMessage="Try adjusting your filters or search" iconClass="text-gray-400" />
        ) : (
          <>
            {/* Screen View Table */}
            <div className="overflow-x-auto border border-gray-200 shadow-xs no-print">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-blue-50 text-gray-700 font-semibold">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 tracking-wider border print-hide-col">Purchase Contract No.</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 tracking-wider border print-hide-col">Sales Contract No.</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 tracking-wider border">Buyer's Name</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 tracking-wider border">Commodity</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 tracking-wider border">Vessel Name</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 tracking-wider border">From</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 tracking-wider border">To</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 tracking-wider border">Sale Quantity</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 tracking-wider border">Delivered Quantity</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 tracking-wider border">Balance QTY (MT)</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 tracking-wider border">Sale Price/MT</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 tracking-wider border">Value of Delivered Qty</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 tracking-wider border">Advance Amt. Received</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 tracking-wider border">Payment Against Sale</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 tracking-wider border">Sales Contract Amount</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 tracking-wider border">Balance to be Received</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 tracking-wider border">Balance to be Received After Adjustment of Advance</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {renderTableRows(data)}
                </tbody>
                <tfoot className="bg-gray-50 font-semibold">
                  {/* Current Page Total */}
                  <tr className="bg-blue-50">
                    <td className="px-3 py-3 text-sm text-gray-900 border font-bold print-hide-col"></td>
                    <td className="px-3 py-3 text-sm text-gray-900 border font-bold print-hide-col"></td>
                    <td className="px-3 py-3 text-sm text-gray-900 border font-bold" colSpan="5">PAGE TOTAL</td>
                    <td className="px-3 py-3 text-sm text-gray-900 border text-right font-bold">{totals.saleQuantity.toLocaleString('en-IN')}</td>
                    <td className="px-3 py-3 text-sm text-gray-900 border text-right font-bold">{totals.soldQuantity.toLocaleString('en-IN')}</td>
                    <td className="px-3 py-3 text-sm text-gray-900 border text-right font-bold">{totals.balanceQuantity.toLocaleString('en-IN')}</td>
                    <td className="px-3 py-3 text-sm text-gray-900 border text-right font-bold">
                      {weightedAverageSalePricePerMT > 0 
                        ? `₹${weightedAverageSalePricePerMT.toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` 
                        : '-'}
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-900 border text-right font-bold">₹{totals.valueOfDeliveredQty.toLocaleString('en-IN')}</td>
                    <td className="px-3 py-3 text-sm text-gray-900 border text-right font-bold">₹{totals.advanceAmount.toLocaleString('en-IN')}</td>
                    <td className="px-3 py-3 text-sm text-gray-900 border text-right font-bold">₹{totals.paymentAgainstSale.toLocaleString('en-IN')}</td>
                    <td className="px-3 py-3 text-sm text-gray-900 border text-right font-bold">₹{totals.salesContractAmount.toLocaleString('en-IN')}</td>
                    <td className="px-3 py-3 text-sm text-gray-900 border text-right font-bold">₹{totals.balanceToBeReceived.toLocaleString('en-IN')}</td>
                    <td className="px-3 py-3 text-sm text-gray-900 border text-right font-bold">₹{totals.balanceAfterAdvanceAdj.toLocaleString('en-IN')}</td>
                  </tr>

                  {/* GRAND TOTAL - Only on Last Page */}
                  {isLastPage && grandTotals && (
                    <tr className="bg-green-100 border-t-2 border-green-500">
                      <td className="px-3 py-3 text-sm text-green-900 border font-extrabold print-hide-col"></td>
                      <td className="px-3 py-3 text-sm text-green-900 border font-extrabold print-hide-col"></td>
                      <td className="px-3 py-3 text-sm text-green-900 border font-extrabold text-center" colSpan="5">GRAND TOTAL</td>
                      <td className="px-3 py-3 text-sm text-green-900 border text-right font-extrabold">{grandTotals.saleQuantity.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-3 text-sm text-green-900 border text-right font-extrabold">{grandTotals.soldQuantity.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-3 text-sm text-green-900 border text-right font-extrabold">{grandTotals.balanceQuantity.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-3 text-sm text-green-900 border text-right font-extrabold">
                        {grandWeightedAverage > 0 
                          ? `₹${grandWeightedAverage.toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` 
                          : '-'}
                      </td>
                      <td className="px-3 py-3 text-sm text-green-900 border text-right font-extrabold">₹{grandTotals.valueOfDeliveredQty.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-3 text-sm text-green-900 border text-right font-extrabold">₹{grandTotals.advanceAmount.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-3 text-sm text-green-900 border text-right font-extrabold">₹{grandTotals.paymentAgainstSale.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-3 text-sm text-green-900 border text-right font-extrabold">₹{grandTotals.salesContractAmount.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-3 text-sm text-green-900 border text-right font-extrabold">₹{grandTotals.balanceToBeReceived.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-3 text-sm text-green-900 border text-right font-extrabold">₹{grandTotals.balanceAfterAdvanceAdj.toLocaleString('en-IN')}</td>
                    </tr>
                  )}
                </tfoot>
              </table>
            </div>

            {/* Print View Table - Hidden on screen, visible on print */}
            {isPrinting && printData.length > 0 && (
              <div className="print-only" style={{width: '100%', overflow: 'hidden'}}>
                <h2 className="text-xl font-bold text-center mb-4">Sale Contract Deadline Monitor</h2>
                <table style={{width: '100%'}}>
                  <thead className="bg-blue-50">
                    <tr>
                      <th className="px-2 py-2 text-left text-xs font-medium border">Buyer's Name</th>
                      <th className="px-2 py-2 text-left text-xs font-medium border">Commodity</th>
                      <th className="px-2 py-2 text-left text-xs font-medium border">Vessel Name</th>
                      <th className="px-2 py-2 text-left text-xs font-medium border">From</th>
                      <th className="px-2 py-2 text-left text-xs font-medium border">To</th>
                      <th className="px-2 py-2 text-left text-xs font-medium border">Sale Quantity</th>
                      <th className="px-2 py-2 text-left text-xs font-medium border">Delivered Quantity</th>
                      <th className="px-2 py-2 text-left text-xs font-medium border">Balance QTY (MT)</th>
                      <th className="px-2 py-2 text-left text-xs font-medium border">Sale Price/MT</th>
                      <th className="px-2 py-2 text-left text-xs font-medium border">Value of Delivered Qty</th>
                      <th className="px-2 py-2 text-left text-xs font-medium border">Advance Amt. Received</th>
                      <th className="px-2 py-2 text-left text-xs font-medium border">Payment Against Sale</th>
                      <th className="px-2 py-2 text-left text-xs font-medium border">Sales Contract Amount</th>
                      <th className="px-2 py-2 text-left text-xs font-medium border">Balance to be Received</th>
                      <th className="px-2 py-2 text-left text-xs font-medium border">Balance to be Received After Adjustment of Advance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {printData.map((row, index) => (
                      <tr key={index}>
                        <td className="px-2 py-2 text-xs border">{row.buyer_name || '-'}</td>
                        <td className="px-2 py-2 text-xs border">{row.commodity_name || '-'}</td>
                        <td className="px-2 py-2 text-xs border">{row.vessel_name || '-'}</td>
                        <td className="px-2 py-2 text-xs border text-center">{row.lifting_period_from || '-'}</td>
                        <td className="px-2 py-2 text-xs border text-center">{row.lifting_period_to || '-'}</td>
                        <td className="px-2 py-2 text-xs border text-right">
                          {row.sale_quantity && parseFloat(row.sale_quantity) > 0 ? parseFloat(row.sale_quantity).toLocaleString('en-IN') : '-'}
                        </td>
                        <td className="px-2 py-2 text-xs border text-right">
                          {row.sold_quantity && parseFloat(row.sold_quantity) > 0 ? parseFloat(row.sold_quantity).toLocaleString('en-IN') : '-'}
                        </td>
                        <td className="px-2 py-2 text-xs border text-right">
                          {row.balance_quantity && parseFloat(row.balance_quantity) > 0 ? parseFloat(row.balance_quantity).toLocaleString('en-IN') : '0'}
                        </td>
                        <td className="px-2 py-2 text-xs border text-right">
                          {row.sale_price_per_mt && parseFloat(row.sale_price_per_mt) > 0 ? `₹${parseFloat(row.sale_price_per_mt).toLocaleString('en-IN')}` : '₹0'}
                        </td>
                        <td className="px-2 py-2 text-xs border text-right">
                          {row.value_of_delivered_qty ? `₹${parseFloat(row.value_of_delivered_qty).toLocaleString('en-IN')}` : '₹0'}
                        </td>
                        <td className="px-2 py-2 text-xs border text-right">
                          {row.advance_amount && parseFloat(row.advance_amount) > 0 ? `₹${parseFloat(row.advance_amount).toLocaleString('en-IN')}` : '₹0'}
                        </td>
                        <td className="px-2 py-2 text-xs border text-right">
                          {row.payment_against_sale && parseFloat(row.payment_against_sale) > 0 ? `₹${parseFloat(row.payment_against_sale).toLocaleString('en-IN')}` : '₹0'}
                        </td>
                        <td className="px-2 py-2 text-xs border text-right">
                          {row.sales_contract_amount && parseFloat(row.sales_contract_amount) > 0 ? `₹${parseFloat(row.sales_contract_amount).toLocaleString('en-IN')}` : '₹0'}
                        </td>
                        <td className="px-2 py-2 text-xs border text-right">
                          {row.balance_to_be_received ? `₹${parseFloat(row.balance_to_be_received).toLocaleString('en-IN')}` : '₹0'}
                        </td>
                        <td className="px-2 py-2 text-xs border text-right">
                          {row.balance_after_advance_adjustment ? `₹${parseFloat(row.balance_after_advance_adjustment).toLocaleString('en-IN')}` : '₹0'}
                        </td>
                      </tr>
                    ))}
                    
                    {/* TOTAL ROW for Print */}
                    <tr className="bg-gray-100 font-bold" style={{pageBreakInside: 'avoid'}}>
                      <td colSpan="5" className="px-2 py-2 text-xs border font-bold">GRAND TOTAL</td>
                      <td className="px-2 py-2 text-xs border text-right font-bold">{printTotals.saleQuantity.toLocaleString('en-IN')}</td>
                      <td className="px-2 py-2 text-xs border text-right font-bold">{printTotals.soldQuantity.toLocaleString('en-IN')}</td>
                      <td className="px-2 py-2 text-xs border text-right font-bold">{printTotals.balanceQuantity.toLocaleString('en-IN')}</td>
                      <td className="px-2 py-2 text-xs border text-right font-bold">
                        {printWeightedAverage > 0 ? `₹${printWeightedAverage.toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : '-'}
                      </td>
                      <td className="px-2 py-2 text-xs border text-right font-bold">₹{printTotals.valueOfDeliveredQty.toLocaleString('en-IN')}</td>
                      <td className="px-2 py-2 text-xs border text-right font-bold">₹{printTotals.advanceAmount.toLocaleString('en-IN')}</td>
                      <td className="px-2 py-2 text-xs border text-right font-bold">₹{printTotals.paymentAgainstSale.toLocaleString('en-IN')}</td>
                      <td className="px-2 py-2 text-xs border text-right font-bold">₹{printTotals.salesContractAmount.toLocaleString('en-IN')}</td>
                      <td className="px-2 py-2 text-xs border text-right font-bold">₹{printTotals.balanceToBeReceived.toLocaleString('en-IN')}</td>
                      <td className="px-2 py-2 text-xs border text-right font-bold">₹{printTotals.balanceAfterAdvanceAdj.toLocaleString('en-IN')}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex justify-end items-center gap-2 mt-3 text-xs no-print">
                <button
                  onClick={handlePrevPage}
                  disabled={page === 1}
                  className={`px-2 py-1 rounded border transition-colors duration-100 ${page === 1 ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'}`}
                >
                  Prev
                </button>
                <span className="text-gray-600">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={handleNextPage}
                  disabled={page === totalPages}
                  className={`px-2 py-1 rounded border transition-colors duration-100 ${page === totalPages ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'}`}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default SalesContractDeadlineMonitor;
